// Light/dark theme management — ported from the task-management UI lesson:
// a single `.dark` class on <html> is the source of truth for CSS; the choice is
// persisted to localStorage and applied BEFORE first paint (flash prevention, see
// index.html) so the dashboard never flickers from light → dark on load.

export type Theme = 'light' | 'dark';
const KEY = 'theme';
export const THEME_EVENT = 'store-cr-theme';

function prefersDark(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

/**
 * Reads the persisted theme. Supports the legacy raw string ("dark" | "light")
 * and a `{ "state": { "theme": "dark" } }` JSON envelope, then falls back to the
 * OS preference, so the value matches whatever the inline boot script picked.
 */
export function getInitialTheme(): Theme {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === 'dark' || raw === 'light') return raw;
    if (raw) {
      const parsed = JSON.parse(raw) as { state?: { theme?: unknown }; theme?: unknown };
      const t = parsed?.state?.theme ?? parsed?.theme;
      if (t === 'dark' || t === 'light') return t;
    }
  } catch {
    /* ignore malformed storage */
  }
  return prefersDark() ? 'dark' : 'light';
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme; // native controls (scrollbars, <select>) follow
}

/** Persist + apply + broadcast so theme-aware widgets (charts) can repaint. */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    /* private mode / storage disabled — apply anyway */
  }
  applyTheme(theme);
  window.dispatchEvent(new CustomEvent<Theme>(THEME_EVENT, { detail: theme }));
}

export function toggleTheme(current: Theme): Theme {
  const next: Theme = current === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}
